import React, { useState, useMemo, useCallback, useEffect } from "react"

import MuiModal from "../shared/mui_modal"
import {
  FormControl,
  Grid,
  Autocomplete,
  TextField,
  InputLabel,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from "@mui/material"
import { devices as devicesClient } from "../../ajax_clients"
import IntuneChip from "@/components/groups/shared/intune_chip"

export default function AssignGroupsModal({
  show,
  onClose,
  onSuccess,
  deviceId,
}) {
  const [groups, setGroups] = useState([])
  const [selectedGroupIds, setSelectedGroupIds] = useState([])
  const [removeOthers, setRemoveOthers] = useState(false)
  const [loading, setLoading] = useState(true)
  const [policy, setPolicy] = useState(null)

  useEffect(() => {
    if (!show) return

    setLoading(true)

    devicesClient.readGroupsForAssignments(deviceId).then(response => {
      setGroups(response.data)
      setPolicy(response.policy)
      setLoading(false)
    })
  }, [deviceId, show, setLoading, setGroups, setPolicy])

  const groupOptions = useMemo(() => {
    if (loading) return []

    return groups.map(group => ({
      id: group.id,
      label: group.name,
      usedForIntune: group.usedForIntune,
    }))
  }, [groups, loading])

  const toggleRemove = useCallback(() => {
    setRemoveOthers(prev => !prev)
  }, [setRemoveOthers])

  const handleGroupsChange = useCallback(
    (_event, newValue) => {
      if (!newValue) {
        setSelectedGroupIds([])
        return
      }

      setSelectedGroupIds(newValue.map(group => group.id))
    },
    [setSelectedGroupIds],
  )

  const handleCommit = useCallback(() => {
    setLoading(true)
    devicesClient
      .createGroupAssignments([deviceId], selectedGroupIds, removeOthers)
      .then(() => {
        setSelectedGroupIds([])
        onSuccess()
        setLoading(false)
        setRemoveOthers(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }, [deviceId, selectedGroupIds, setLoading, onSuccess, removeOthers])

  if (!show) return null

  return (
    <MuiModal
      titleText="Assign Groups"
      buttonText="Assign"
      onClose={onClose}
      onCommit={handleCommit}
    >
      <Grid
        item
        xs={12}
        container
        alignItems="center"
        spacing={2}
        sx={{ minHeight: "120px" }}
      >
        <Grid item xs={2}>
          <InputLabel id="group-select-label">Groups</InputLabel>
        </Grid>
        <Grid item xs={9}>
          <FormControl fullWidth>
            <Autocomplete
              multiple
              id="group-select"
              aria-labelledby="group-select-label"
              options={groupOptions}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <div>
                    {option.label}
                    {option.usedForIntune && <IntuneChip sx={{ ml: 1 }} />}
                  </div>
                </li>
              )}
              getOptionDisabled={option =>
                option.usedForIntune && !policy?.modifyIntune
              }
              disableCloseOnSelect
              loading={loading}
              onChange={handleGroupsChange}
              disabled={loading}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="standard"
                  placeholder="Select groups"
                />
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
        container
        alignItems="center"
        spacing={2}
        sx={{ minHeight: "120px" }}
      >
        <Grid item xs={2} />
        <Grid item xs={9}>
          <FormControlLabel
            label="Remove from all other directly assigned groups"
            control={
              <Checkbox checked={removeOthers} onChange={toggleRemove} />
            }
          />
          <FormHelperText>
            When checked, the device will be removed from any other currently
            assigned static groups and directly assigned dynamic groups.{" "}
            {policy?.modifyIntune ? "" : "Intune groups will not be removed."}
          </FormHelperText>
        </Grid>
      </Grid>
    </MuiModal>
  )
}
